import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

const PasswordInput = ({ testid, value, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1">
      <Input data-testid={testid} type={show ? "text" : "password"} required autoCapitalize="none" autoCorrect="off" spellCheck={false} value={value} onChange={onChange} className="rounded-none h-11 bg-white pr-11" />
      <button type="button" onClick={() => setShow(!show)} data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#575757]">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
    </div>
  );
};

const Shell = ({ title, sub, children }) => (
  <div className="max-w-6xl mx-auto px-5 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-12">
    <div className="lg:col-span-5 hidden lg:block relative overflow-hidden">
      <img src="https://images.pexels.com/photos/22730367/pexels-photo-22730367.jpeg?auto=compress&cs=tinysrgb&w=1000" alt="Abidjan port" className="w-full h-full object-cover min-h-[480px]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 to-transparent flex items-end p-8">
        <p className="font-display text-2xl text-white leading-snug">“We contextualise, analyse, and illuminate.”</p>
      </div>
    </div>
    <div className="lg:col-span-6 lg:col-start-7 self-center">
      <p className="overline text-[#E5332A]">Africa Brew Brief</p>
      <h1 className="font-display text-4xl font-bold mt-3">{title}</h1>
      <p className="text-[#575757] mt-3">{sub}</p>
      <div className="mt-8">{children}</div>
    </div>
  </div>
);

const useNext = () => {
  const [p] = useSearchParams();
  return p.get("next");
};

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const next = useNext();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const u = await login(f.email, f.password); navigate(next || (u.role === "admin" ? "/admin" : "/account")); } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <Shell title="Sign in" sub="Welcome back to the brief.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><Label>Email</Label><Input data-testid="login-email-input" type="email" autoCapitalize="none" autoCorrect="off" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none h-11 mt-1 bg-white" /></div>
        <div><Label>Password</Label><PasswordInput testid="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        {err && <p data-testid="login-error" className="text-sm text-[#E5332A]">{err}</p>}
        <button data-testid="login-submit-btn" className="w-full bg-[#0A0A0A] text-white py-3 text-sm hover:bg-[#E5332A] transition-colors">Sign in</button>
        <p className="text-sm text-[#575757]">New here? <Link to={`/register${next ? `?next=${next}` : ""}`} data-testid="go-register-link" className="text-[#E5332A] link-underline">Create a free account</Link></p>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const next = useNext();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await register(f.name, f.email, f.password); navigate(next || "/pricing"); } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <Shell title="Create your account" sub="Free daily briefs, newsletters and selected analysis.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div><Label>Full name</Label><Input data-testid="register-name-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none h-11 mt-1 bg-white" /></div>
        <div><Label>Work email</Label><Input data-testid="register-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none h-11 mt-1 bg-white" /></div>
        <div><Label>Password</Label><Input data-testid="register-password-input" type="password" minLength={6} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-none h-11 mt-1 bg-white" /></div>
        {err && <p data-testid="register-error" className="text-sm text-[#E5332A]">{err}</p>}
        <button data-testid="register-submit-btn" className="w-full bg-[#E5332A] text-white py-3 text-sm hover:bg-[#B8241C] transition-colors">Create account</button>
        <p className="text-sm text-[#575757]">Already a reader? <Link to="/login" className="text-[#E5332A] link-underline">Sign in</Link></p>
      </form>
    </Shell>
  );
}
